import { useEffect, useState } from 'react';
import {
  Settings,
  Loader2,
  CheckCircle,
  XCircle,
  Send,
  Wifi,
  Inbox,
  Mail,
  RefreshCw,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { settingsService, type ProductEmailConfigInput } from '../services/settings.service';
import { useProduct } from '../contexts/ProductContext';

interface FormData {
  user: string;
  pass: string;
  fromEmail: string;
  fromName: string;
  smtpHost: string;
  smtpPort: number;
  imapHost: string;
  imapPort: number;
  imapSecure: boolean;
}

const DEFAULT_VALUES: FormData = {
  user: '',
  pass: '',
  fromEmail: '',
  fromName: '',
  smtpHost: 'smtp.hostinger.com',
  smtpPort: 465,
  imapHost: 'imap.hostinger.com',
  imapPort: 993,
  imapSecure: true,
};

export default function SettingsPage() {
  const qc = useQueryClient();
  const {
    products,
    selectedProductId,
    setSelectedProductId,
    isLoading: productsLoading,
  } = useProduct();
  const [smtpResult, setSmtpResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [imapResult, setImapResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [testEmailResult, setTestEmailResult] = useState<{
    success: boolean;
    error?: string;
  } | null>(null);
  const [pollResult, setPollResult] = useState<{
    scanned?: number;
    bouncesFound?: number;
    bouncesMatched?: number;
    skipped?: string;
  } | null>(null);
  const [isTestingSmtp, setIsTestingSmtp] = useState(false);
  const [isTestingImap, setIsTestingImap] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  const productId = selectedProductId ?? 0;

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['settings', 'email', productId],
    queryFn: () => settingsService.getConfig(productId),
    enabled: productId > 0,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormData>({ defaultValues: DEFAULT_VALUES });

  useEffect(() => {
    if (config) {
      reset({
        user: config.user || '',
        pass: config.pass || '',
        fromEmail: config.fromEmail || '',
        fromName: config.fromName || '',
        smtpHost: config.smtpHost || 'smtp.hostinger.com',
        smtpPort: config.smtpPort || 465,
        imapHost: config.imapHost || 'imap.hostinger.com',
        imapPort: config.imapPort || 993,
        imapSecure: config.imapSecure !== false,
      });
    } else if (productId > 0 && !configLoading) {
      reset(DEFAULT_VALUES);
    }
  }, [config, productId, configLoading, reset]);

  // Reset transient feedback when switching products
  useEffect(() => {
    setSmtpResult(null);
    setImapResult(null);
    setTestEmailResult(null);
    setPollResult(null);
  }, [productId]);

  const saveMutation = useMutation({
    mutationFn: (data: FormData) => {
      const payload: ProductEmailConfigInput = {
        user: data.user,
        pass: data.pass,
        fromEmail: data.fromEmail,
        fromName: data.fromName.trim() || undefined,
        smtpHost: data.smtpHost,
        smtpPort: data.smtpPort,
        imapHost: data.imapHost || undefined,
        imapPort: data.imapPort,
        imapSecure: data.imapSecure,
      };
      return settingsService.saveConfig(productId, payload);
    },
    onSuccess: () => {
      toast.success('Configuração salva com sucesso');
      qc.invalidateQueries({ queryKey: ['settings', 'email', productId] });
    },
    onError: () => {
      toast.error('Erro ao salvar configuração');
    },
  });

  async function onSubmit(data: FormData) {
    if (!productId) {
      return;
    }
    await saveMutation.mutateAsync(data);
  }

  async function handleTestSmtp() {
    setIsTestingSmtp(true);
    setSmtpResult(null);
    try {
      const result = await settingsService.testSmtpConnection(productId);
      setSmtpResult(result);
      if (result.success) {
        toast.success('Conexão SMTP testada com sucesso!');
      } else {
        toast.error(`Falha SMTP: ${result.error}`);
      }
    } catch {
      toast.error('Erro ao testar conexão SMTP');
    } finally {
      setIsTestingSmtp(false);
    }
  }

  async function handleTestImap() {
    setIsTestingImap(true);
    setImapResult(null);
    try {
      const result = await settingsService.testImapConnection(productId);
      setImapResult(result);
      if (result.success) {
        toast.success('Conexão IMAP testada com sucesso!');
      } else {
        toast.error(`Falha IMAP: ${result.error}`);
      }
    } catch {
      toast.error('Erro ao testar conexão IMAP');
    } finally {
      setIsTestingImap(false);
    }
  }

  async function handleSendTestEmail() {
    if (!testEmail) {
      toast.error('Digite um email para enviar o teste');
      return;
    }
    setIsSendingTest(true);
    setTestEmailResult(null);
    try {
      const result = await settingsService.sendTestEmail(productId, testEmail);
      setTestEmailResult(result);
      if (result.success) {
        toast.success(`Email de teste enviado para ${testEmail}!`);
      } else {
        toast.error(`Falha: ${result.error}`);
      }
    } catch {
      toast.error('Erro ao enviar email de teste');
    } finally {
      setIsSendingTest(false);
    }
  }

  async function handleRunPoll(includeRead = false) {
    if (includeRead) {
      const ok = window.confirm(
        'Varrer a caixa completa (até 60 dias, inclusive emails já lidos)? Pode demorar mais.'
      );
      if (!ok) {
        return;
      }
    }
    setIsPolling(true);
    setPollResult(null);
    try {
      const result = await settingsService.runImapPoll({ includeRead });
      const summary = result.total ?? result;
      setPollResult({
        scanned: summary.scanned,
        bouncesFound: summary.bouncesFound,
        bouncesMatched: summary.bouncesMatched,
        skipped: result.skipped,
      });
      if (result.skipped) {
        toast.error(`Verificação: ${result.skipped}`);
      } else {
        toast.success(
          `Verificação concluída: ${summary.scanned} mensagens, ${summary.bouncesMatched} bounces atualizados`
        );
      }
    } catch {
      toast.error('Erro ao verificar bounces');
    } finally {
      setIsPolling(false);
    }
  }

  if (productsLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-ink-primary" />
      </div>
    );
  }

  if (!products.length) {
    return (
      <div className="space-y-6 max-w-2xl">
        <div className="flex items-center gap-3">
          <Settings className="w-6 h-6 text-ink-primary" />
          <h1 className="text-2xl font-bold text-ink-primary">Configurações de Email</h1>
        </div>
        <p className="text-sm text-ink-tertiary">Você não tem produtos disponíveis.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center gap-3">
        <Settings className="w-6 h-6 text-ink-primary" />
        <h1 className="text-2xl font-bold text-ink-primary">Configurações de Email</h1>
      </div>

      {/* Product selector */}
      <div className="bg-surface rounded-xl border border-border p-4">
        <label className="block text-xs font-medium text-ink-tertiary mb-1">
          Configuração por produto
        </label>
        <select
          value={productId || ''}
          onChange={(e) => setSelectedProductId(Number(e.target.value))}
          className="w-full px-3 py-2 border border-border-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent"
        >
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      {configLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-ink-primary" />
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Credenciais */}
          <section className="bg-surface rounded-xl border border-border p-6">
            <h2 className="font-serif text-lg font-semibold text-ink-primary mb-1">Credenciais</h2>
            <p className="text-sm text-ink-tertiary mb-4">
              Usuário e senha compartilhados entre SMTP e IMAP.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">
                  Usuário / Email
                </label>
                <input
                  type="text"
                  placeholder="usuario@exemplo.com"
                  {...register('user', { required: 'Usuário é obrigatório' })}
                  className="w-full px-3 py-2 border border-border-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent"
                />
                {errors.user && <p className="mt-1 text-xs text-danger">{errors.user.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">Senha</label>
                <input
                  type="password"
                  placeholder="••••••••"
                  {...register('pass', { required: 'Senha é obrigatória' })}
                  className="w-full px-3 py-2 border border-border-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent"
                />
                {errors.pass && <p className="mt-1 text-xs text-danger">{errors.pass.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">
                  Email de envio (From)
                </label>
                <input
                  type="email"
                  placeholder="nome@exemplo.com"
                  {...register('fromEmail', {
                    required: 'Email de envio é obrigatório',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email inválido' },
                  })}
                  className="w-full px-3 py-2 border border-border-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent"
                />
                {errors.fromEmail && (
                  <p className="mt-1 text-xs text-danger">{errors.fromEmail.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">
                  Nome do remetente
                </label>
                <input
                  type="text"
                  placeholder="CRM"
                  maxLength={100}
                  {...register('fromName', {
                    maxLength: { value: 100, message: 'No máximo 100 caracteres' },
                  })}
                  className="w-full px-3 py-2 border border-border-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent"
                />
                <p className="mt-1 text-xs text-ink-tertiary">
                  Aparece como remetente na caixa de entrada. Vazio usa &quot;CRM&quot;.
                </p>
                {errors.fromName && (
                  <p className="mt-1 text-xs text-danger">{errors.fromName.message}</p>
                )}
              </div>
            </div>
          </section>

          {/* SMTP */}
          <section className="bg-surface rounded-xl border border-border p-6">
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-5 h-5 text-ink-primary" />
              <h2 className="font-serif text-lg font-semibold text-ink-primary">SMTP — Envio</h2>
            </div>
            <p className="text-sm text-ink-tertiary mb-4">
              Servidor de envio de campanhas para este produto.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">
                  Host SMTP
                </label>
                <input
                  type="text"
                  placeholder="smtp.hostinger.com"
                  {...register('smtpHost', { required: 'Host SMTP é obrigatório' })}
                  className="w-full px-3 py-2 border border-border-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent"
                />
                {errors.smtpHost && (
                  <p className="mt-1 text-xs text-danger">{errors.smtpHost.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">
                  Porta SMTP
                </label>
                <input
                  type="number"
                  placeholder="465"
                  {...register('smtpPort', {
                    required: 'Porta é obrigatória',
                    valueAsNumber: true,
                    min: { value: 1, message: 'Porta inválida' },
                    max: { value: 65535, message: 'Porta inválida' },
                  })}
                  className="w-full px-3 py-2 border border-border-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent"
                />
                {errors.smtpPort && (
                  <p className="mt-1 text-xs text-danger">{errors.smtpPort.message}</p>
                )}
              </div>

              {smtpResult && (
                <div
                  className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                    smtpResult.success
                      ? 'bg-subtle text-accent border border-accent'
                      : 'bg-subtle text-danger border border-danger'
                  }`}
                >
                  {smtpResult.success ? (
                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 flex-shrink-0" />
                  )}
                  <span>
                    {smtpResult.success
                      ? 'Conexão SMTP estabelecida!'
                      : `Falha SMTP: ${smtpResult.error}`}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={handleTestSmtp}
                  disabled={isTestingSmtp || isDirty}
                  title={isDirty ? 'Salve antes de testar' : 'Testar conexão SMTP'}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-secondary bg-subtle rounded-lg hover:bg-subtle disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isTestingSmtp ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Wifi className="w-4 h-4" />
                  )}
                  Testar SMTP
                </button>
              </div>

              {/* Send test email */}
              <div className="border-t border-border pt-4 mt-2">
                <label className="block text-sm font-medium text-ink-secondary mb-1">
                  Enviar email de teste
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="email"
                    placeholder="seu@email.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="flex-1 min-w-[200px] px-3 py-2 border border-border-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={handleSendTestEmail}
                    disabled={isSendingTest || !testEmail || isDirty}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-secondary bg-subtle rounded-lg hover:bg-subtle disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSendingTest ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    Enviar Teste
                  </button>
                </div>
                {testEmailResult && (
                  <div
                    className={`mt-2 flex items-center gap-2 p-3 rounded-lg text-sm ${
                      testEmailResult.success
                        ? 'bg-subtle text-accent border border-accent'
                        : 'bg-subtle text-danger border border-danger'
                    }`}
                  >
                    {testEmailResult.success ? (
                      <CheckCircle className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 flex-shrink-0" />
                    )}
                    <span>
                      {testEmailResult.success
                        ? `Email enviado para ${testEmail}!`
                        : `Falha: ${testEmailResult.error}`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* IMAP */}
          <section className="bg-surface rounded-xl border border-border p-6">
            <div className="flex items-center gap-2 mb-1">
              <Inbox className="w-5 h-5 text-ink-primary" />
              <h2 className="font-serif text-lg font-semibold text-ink-primary">
                IMAP — Detecção de Bounces
              </h2>
            </div>
            <p className="text-sm text-ink-tertiary mb-4">
              Lê mensagens MAILER-DAEMON na caixa e marca destinatários que não receberam. Deixe
              Host IMAP em branco para desativar.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">
                  Host IMAP
                </label>
                <input
                  type="text"
                  placeholder="imap.hostinger.com"
                  {...register('imapHost')}
                  className="w-full px-3 py-2 border border-border-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">
                  Porta IMAP
                </label>
                <input
                  type="number"
                  placeholder="993"
                  {...register('imapPort', {
                    valueAsNumber: true,
                    min: { value: 1, message: 'Porta inválida' },
                    max: { value: 65535, message: 'Porta inválida' },
                  })}
                  className="w-full px-3 py-2 border border-border-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus-visible:ring-accent focus:border-transparent"
                />
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" {...register('imapSecure')} />
                <span className="text-ink-secondary">Conexão segura (TLS/SSL)</span>
              </label>

              {imapResult && (
                <div
                  className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                    imapResult.success
                      ? 'bg-subtle text-accent border border-accent'
                      : 'bg-subtle text-danger border border-danger'
                  }`}
                >
                  {imapResult.success ? (
                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 flex-shrink-0" />
                  )}
                  <span>
                    {imapResult.success
                      ? 'Conexão IMAP estabelecida!'
                      : `Falha IMAP: ${imapResult.error}`}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={handleTestImap}
                  disabled={isTestingImap || isDirty}
                  title={isDirty ? 'Salve antes de testar' : 'Testar conexão IMAP'}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-secondary bg-subtle rounded-lg hover:bg-subtle disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isTestingImap ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Wifi className="w-4 h-4" />
                  )}
                  Testar IMAP
                </button>

                <button
                  type="button"
                  onClick={() => handleRunPoll(false)}
                  disabled={isPolling || isDirty}
                  title="Verifica bounces não lidos dos últimos 60 dias"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-secondary bg-subtle rounded-lg hover:bg-subtle disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isPolling ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Verificar Bounces agora
                </button>

                <button
                  type="button"
                  onClick={() => handleRunPoll(true)}
                  disabled={isPolling || isDirty}
                  title="Varre toda a caixa nos últimos 60 dias (inclusive emails já lidos)"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-ink-secondary border border-border-strong rounded-lg hover:bg-subtle disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isPolling ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Varrer caixa completa
                </button>
              </div>

              {pollResult && !pollResult.skipped && (
                <div className="flex items-center gap-2 p-3 rounded-lg text-sm bg-subtle text-ink-secondary border border-border">
                  <RefreshCw className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {pollResult.scanned} mensagem(ns) analisada(s) · {pollResult.bouncesFound}{' '}
                    bounce(s) detectado(s) · {pollResult.bouncesMatched} atualizado(s)
                  </span>
                </div>
              )}
            </div>
          </section>

          <div className="sticky bottom-4 z-10 flex justify-end">
            <button
              type="submit"
              disabled={saveMutation.isPending || !isDirty}
              className="flex items-center gap-2 px-6 py-3 text-sm font-medium text-canvas bg-ink-primary rounded-lg hover:bg-ink-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg"
            >
              {saveMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Salvar Configuração
            </button>
          </div>
        </form>
      )}

      <section className="rounded-lg border border-border bg-surface p-5">
        <h2 className="font-serif text-xl font-semibold text-ink-primary mb-1">Aparência</h2>
        <p className="text-sm text-ink-tertiary">
          O tema (claro / escuro / sistema) agora é controlado pelo botão no canto superior direito.
        </p>
      </section>
    </div>
  );
}
